import { motion, type Variant } from "framer-motion"
import React, { useEffect, useState } from "react"

interface BrackyHeroQuoteProps {
  liveSportsTrending: boolean
  ellipsisRef: React.MutableRefObject<HTMLSpanElement | null>
  onAnimationComplete: () => void
}

const wordVariants: Record<string, Variant> = {
  initial: (isGradient: boolean) => ({
    y: "0.25em",
    opacity: 0,
    ...(isGradient ? {} : { color: "#ffffff" }),
  }),
  animate: (isGradient: boolean) => ({
    y: 0,
    opacity: 1,
    ...(isGradient ? {} : { color: "#ffffff" }),
    transition: { duration: 0.4, ease: "easeOut" },
  }),
}

const maskedWordVariants: Record<string, Variant> = {
  initial: { y: "0.25em", opacity: 0 },
  animate: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.4, ease: "easeOut" },
  },
}

export const BrackyHeroQuote = React.memo(function BrackyHeroQuote({
  liveSportsTrending,
  ellipsisRef,
  onAnimationComplete,
}: BrackyHeroQuoteProps) {
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 725 : false)
  const [isXL, setIsXL] = useState(typeof window !== "undefined" ? window.innerWidth >= 1280 : true)
  const [isMDLG, setIsMDLG] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 725 && window.innerWidth < 1024 : false,
  )

  useEffect(() => {
    if (typeof window === "undefined") return

    const handleResize = () => {
      setIsMobile(window.innerWidth < 725)
      setIsXL(window.innerWidth >= 1280)
      setIsMDLG(window.innerWidth >= 725 && window.innerWidth < 1024)
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const buildWords = () => {
    const desktopSentence = "BrackyAI™ transforms social feeds into Live Sports Markets™ fueled by Fans…"
    const lgSentence = "BrackyAI™ transforms social feeds into Live Sports Markets™ fueled by Fans…"
    const mobileSentence = "BrackyAI™ transforms social feeds into Live Sports Markets™ fueled by Fans."

    let sentence = isMobile ? mobileSentence : isXL ? desktopSentence : lgSentence

    if (isMobile) {
      sentence = sentence.replace(/into\s+Live/gi, "into\u00A0Live")
      sentence = sentence.replace(/Sports\s+Markets™/gi, "Sports\u00A0Markets™")
    } else if (isXL) {
      sentence = sentence.replace(/Live\s+Sports\s+Markets™/gi, "Live\u00A0Sports\u00A0Markets™")
      sentence = sentence.replace(/social\s+feeds/gi, "social feeds")
    } else if (isMDLG) {
      sentence = sentence.replace(/into\s+Live/gi, "into\u00A0Live")
      sentence = sentence.replace(/Live\s+Sports\s+Markets™/gi, "Live\u00A0Sports\u00A0Markets™")
      sentence = sentence.replace(/social\s+feeds/gi, "social\u00A0feeds")
    } else {
      sentence = sentence.replace(/Live\s+Sports\s+Markets™/gi, "Live\u00A0Sports\u00A0Markets™")
    }

    const words = sentence.split(" ").filter((w) => w.length > 0)
    const wordCounts = new Map<string, number>()

    return words.flatMap((word, idx) => {
      // Generate unique key using word content and occurrence count
      const wordKey = word.toLowerCase()
      const occurrenceCount = wordCounts.get(wordKey) || 0
      wordCounts.set(wordKey, occurrenceCount + 1)
      const uniqueKey = `${word}-${occurrenceCount}`

      const isMaskedWord = word === "BrackyAI™"
      const isLiveSportsMarketsWord =
        word.toLowerCase().includes("live\u00a0sports\u00a0markets™") ||
        word.toLowerCase().includes("live sports markets™") ||
        word.toLowerCase().includes("live\u00a0sports\u00a0markets") ||
        word.toLowerCase().includes("live sports markets") ||
        word.toLowerCase().includes("live\u00a0sports\u00a0markets…") ||
        word.toLowerCase().includes("live sports markets…")
      const isLiveSportsWord = word.toLowerCase() === "live\u00a0sports" || word.toLowerCase() === "live sports"
      const isSportsMarketsWord =
        word.toLowerCase() === "sports\u00a0markets™" ||
        word.toLowerCase() === "sports\u00a0markets" ||
        word.toLowerCase() === "sports markets™" ||
        word.toLowerCase() === "sports markets"
      const hasEllipsis = word.endsWith("…")
      let cleanWord = hasEllipsis ? word.slice(0, -1) : word
      const isStandaloneLiveWord = cleanWord.toLowerCase() === "live"
      const isIntoLiveWord = word.toLowerCase() === "into\u00a0live" || word.toLowerCase() === "into live"

      cleanWord = cleanWord.replace(/[.]+$/, "")

      const lowered = cleanWord.toLowerCase()
      const isMarketsWord = lowered === "markets"
      const isDrivenByFansWord = ["driven", "by", "fans", "into"].includes(lowered)
      const isLastWord = idx === words.length - 1

      const displayWord = isMarketsWord && isMobile && word.endsWith(".") ? "Markets" : cleanWord

      const isSocialFeedsToken = lowered.includes("social\u00a0feeds")
      const isBoldWord =
        [
          "transforms",
          "social",
          "feeds",
          "into",
          "for",
          "fueled",
          "by",
          "invested",
          "driven",
          "brought",
          "life",
          "by",
          "to",
          "predicted",
          "to",
          "life",
          "",
        ].includes(lowered) || isSocialFeedsToken
      const fontWeight = isBoldWord ? "font-bold" : "font-extrabold"
      const letterSpacing = isBoldWord ? "-0.09rem" : "-.1rem"

      if (isIntoLiveWord) {
        const combinedSpan = (
          <motion.span
            key={`word-${uniqueKey}`}
            className={`inline-block ${isLastWord ? "" : "mr-2"}`}
            variants={wordVariants}
            custom={true}
          >
            <span
              className="font-bold text-white bg-clip-text text-transparent -mr-[.05rem]"
              style={{ letterSpacing: "-0.09rem" }}
            >
              into{" "}
            </span>
            <span
              className={`font-extrabold ${liveSportsTrending ? "shiny-text-trending bg-clip-text text-transparent" : "text-[#22FF65]"}`}
              style={{ letterSpacing: "-0.1rem" }}
            >
              Live
            </span>
          </motion.span>
        )
        const out: React.ReactNode[] = [combinedSpan]
        return out
      }

      const shouldHaveTrailingSpace = isLastWord
        ? false
        : !(isMDLG && (isLiveSportsMarketsWord || isMarketsWord || isSportsMarketsWord || lowered === "into"))

      const classes =
        lowered === "into" || isDrivenByFansWord
          ? `inline-block ${shouldHaveTrailingSpace ? "mr-2" : ""} ${fontWeight} text-white bg-clip-text text-transparent`
          : `inline-block ${shouldHaveTrailingSpace ? "mr-2" : ""} ${fontWeight} ${
              isMaskedWord
                ? "pb-[1em] -mb-[1em] text-transparent bg-clip-text"
                : isLiveSportsMarketsWord ||
                    isLiveSportsWord ||
                    isMarketsWord ||
                    isSportsMarketsWord ||
                    isStandaloneLiveWord
                  ? "text-transparent bg-clip-text"
                  : "text-white bg-clip-text text-transparent"
            } ${cleanWord === "Markets" ? "ml-0 sm:ml-0" : ""}`

      const element = (
        <motion.span
          key={`word-${uniqueKey}`}
          className={`${classes} ${cleanWord === "social feeds" ? "md:whitespace-nowrap" : ""} relative ${isMobile && isMarketsWord ? "-ml-3" : ""} ${isMDLG && (isLiveSportsMarketsWord || isLiveSportsWord) ? "ml-[.5rem]" : ""} ${(isMobile || isMDLG) && isMaskedWord ? "ml-3" : ""} ${isSportsMarketsWord ? "ml-2" : ""} ${!(isMobile || isMDLG) && cleanWord.toLowerCase() === "transforms" ? "-ml-[0.2rem]" : ""} ${isXL && isMaskedWord ? "xl:ml-[.8rem]" : ""}`}
          style={{ letterSpacing }}
          variants={isMaskedWord ? maskedWordVariants : wordVariants}
          {...(isMaskedWord || lowered === "into" || isDrivenByFansWord
            ? {}
            : {
                custom:
                  isLiveSportsMarketsWord ||
                  isLiveSportsWord ||
                  isMarketsWord ||
                  isSportsMarketsWord ||
                  isStandaloneLiveWord,
              })}
        >
          {isMaskedWord ? (
            <span className="relative inline-block">
              <span
                className="bg-linear-to-r from-[#30DFFF] to-[#0074FF] bg-clip-text text-transparent shiny-text-ai-sports-agent"
                style={{
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  overflow: "visible",
                  letterSpacing: "-0.1rem",
                }}
              >
                {isMDLG ? "\u00A0" : ""}BrackyAI
                <span className="align-super font-normal text-[0.5em]">™</span>
              </span>
              <span
                className="pointer-events-none absolute top-0 left-0 bg-clip-text text-transparent opacity-60"
                style={{
                  WebkitTextFillColor: "transparent",
                  backgroundImage: "linear-gradient(to bottom, transparent 0%, transparent 40%, #001CFF 100%)",
                  letterSpacing: "-0.1rem",
                  paddingBottom: "0.2em",
                }}
                aria-hidden="true"
              >
                {isMDLG ? "\u00A0" : ""}BrackyAI
                <span className="align-super font-normal text-[0.5em]">™</span>
              </span>
            </span>
          ) : lowered === "into" || isDrivenByFansWord ? (
            <span style={{ color: "#ffffff", background: "none", WebkitTextFillColor: "#ffffff", letterSpacing }}>
              {cleanWord}
            </span>
          ) : isLiveSportsMarketsWord ||
            isLiveSportsWord ||
            isMarketsWord ||
            isSportsMarketsWord ||
            isStandaloneLiveWord ? (
            <span className="relative inline-block">
              <span
                className={`relative font-extrabold ${
                  liveSportsTrending ? "shiny-text-trending bg-clip-text text-transparent" : "text-[#22FF65]"
                }`}
                style={{ letterSpacing: "-0.1rem" }}
              >
                {displayWord.includes("™") ? (
                  <>
                    {displayWord.replace("™", "")}
                    <span className="relative font-normal text-[0.45em] lg:text-[05.em] text-[#22FF65] top-[-0.59em] left-[-0.07em] lg:top-[-0.6em] lg:hidden">
                      ™
                    </span>
                  </>
                ) : (
                  displayWord
                )}
              </span>
            </span>
          ) : cleanWord === "social feeds" ? (
            "social\u00A0feeds"
          ) : (
            displayWord
          )}
          {hasEllipsis && !isMobile && (
            <span
              ref={ellipsisRef}
              className="ellipsis-dots shiny-text-trending absolute left-full ml-0.5"
              aria-hidden="true"
            />
          )}
        </motion.span>
      )

      const out: React.ReactNode[] = [element]

      if (isMaskedWord) {
        out.push(<br key={`br-after-${uniqueKey}`} className="block mdlg:block lg:hidden" />)
      }

      if (isMobile && cleanWord.toLowerCase() === "social") {
        out.push(<br key={`br-mobile-${uniqueKey}`} className="block mdlg:hidden" />)
      }

      if (isMDLG && cleanWord.toLowerCase() === "social") {
        out.push(<br key={`br-mdlg-${uniqueKey}`} className="hidden mdlg:block lg:hidden" />)
      }

      if (isMDLG && (cleanWord.toLowerCase() === "social\u00a0feeds" || cleanWord.toLowerCase() === "social feeds")) {
        out.push(<br key={`br-mdlg-${uniqueKey}`} className="hidden mdlg:block lg:hidden" />)
      }

      if (isMobile && cleanWord.toLowerCase() === "live") {
        out.push(<br key={`br-mobile-${uniqueKey}`} className="block mdlg:hidden" />)
      }

      if (isMDLG && cleanWord.toLowerCase() === "live") {
        out.push(<br key={`br-mdlg-${uniqueKey}`} className="hidden mdlg:block lg:hidden" />)
      }

      if (isMarketsWord || isLiveSportsMarketsWord || isSportsMarketsWord) {
        out.push(<br key={`br-mdlg-markets-${uniqueKey}`} className="hidden mdlg:block lg:hidden" />)
      }

      if (isMobile && isSportsMarketsWord) {
        out.push(<br key={`br-mobile-sports-${uniqueKey}`} className="block mdlg:hidden" />)
      }

      if (isMDLG && isSportsMarketsWord) {
        out.push(<br key={`br-mdlg-sports-alt-${uniqueKey}`} className="hidden mdlg:block lg:hidden" />)
      }

      if (lowered === "feeds") {
        out.push(<br key={`br-lg-${uniqueKey}`} className="hidden lg:block xl:hidden" />)
      }
      if (isLiveSportsMarketsWord || isSportsMarketsWord) {
        out.push(<br key={`br-lg-markets-${uniqueKey}`} className="hidden lg:block xl:hidden" />)
      }

      if (lowered === "into") {
        out.push(<br key={`br-xl-${uniqueKey}`} className="hidden xl:block" />)
      }

      if (isMobile && lowered === "fans") {
        out.push(
          <span
            key="mobile-green-dot"
            className="inline-block w-[0.52rem] h-[0.52rem] rounded-full bg-[#22FF65] ml-[0.15rem] relative top-[0.02rem] shadow-[0_0_3px_#22FF65BD,0_0_8px_#22FF65BD,0_0_15px_#22FF65BD]"
          />,
        )
      }

      return out
    }) as React.ReactNode[]
  }

  return (
    <motion.h2
      className="leading-none pb-[0.3em] max-w-full mx-auto mb-0 sm:-mt-[.62rem] text-h2 text-center"
      variants={{
        initial: {},
        animate: {
          transition: {
            staggerChildren: 0.04,
          },
        },
      }}
      key={isMobile ? "heading-mobile" : "heading-desktop"}
      initial="initial"
      animate="animate"
      onAnimationComplete={onAnimationComplete}
    >
      {buildWords()}
    </motion.h2>
  )
})
